import React, { useState, useEffect } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import GeneradorMinutaV2 from './v2/GeneradorMinutaV2';
import Inicio from './v2/Inicio';
import DetalleReunion from './v2/DetalleReunion';
import FirmarActa from './v2/FirmarActa';
import Configuracion from './v2/Configuracion';
import Bienvenida from './v2/Bienvenida';
import { TemaProvider } from './v2/tema';
import { userIdDeCuenta, usarTokenDeSesion } from '../services/minutasV2Service';
import { contextoGuardado, guardarContexto } from '../services/contextoDeVista';

const AuthenticatedApp = () => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // Vistas por hash. La v2 es la app: al entrar sin hash se va ahí.
  //
  //   (sin hash)   → inicio de la v2
  //   #v2/reunion  → el detalle de una reunión
  //   #v2/generar  → generar una minuta
  //   #v2/config   → formatos de minuta, apariencia y sesión
  //
  // Evita meter un router solo para esto durante la migración.
  const [hash, setHash] = useState(() => window.location.hash);

  useEffect(() => {
    const alCambiarHash = () => setHash(window.location.hash);
    window.addEventListener('hashchange', alCambiarHash);
    return () => window.removeEventListener('hashchange', alCambiarHash);
  }, []);

  // ── La pagina de firma ────────────────────────────────────────────────
  //
  // `#firmar/<token>` va aparte de todo lo demas y se mira ANTES que la sesion,
  // a proposito: quien llega ahi viene del enlace de un correo y puede no haber
  // abierto nunca esta aplicacion. Mandarle a la pantalla de bienvenida —«Entra
  // con tu cuenta de Microsoft»— sin decirle siquiera que acta es ni quien se la
  // manda es pedirle credenciales a ciegas, que es exactamente lo que se le
  // ensena a la gente a no hacer.
  //
  // Asi que primero ve QUE se le pide, y entra cuando va a firmar.
  const firma = (hash.match(/^#firmar\/([A-Za-z0-9_-]{16,90})$/) || [])[1] || null;

  const vistaV2 = hash === '#v2/generar' ? 'generar'
    : hash === '#v2/reunion' ? 'reunion'
    : hash === '#v2/config' ? 'config'
    : 'inicio';

  // El .docx, la reunión y la minuta elegidos en el inicio viajan a la vista
  // siguiente. Van aquí y no en la URL porque una reunión de Graph es un objeto
  // entero, no un identificador que se pueda volver a resolver solo — y por eso
  // se apuntan en la pestaña: si no, la recarga los perdía. El porqué, en
  // `contextoDeVista`.
  const [contextoV2, setContextoV2] = useState(contextoGuardado);

  useEffect(() => { guardarContexto(contextoV2); }, [contextoV2]);

  const irAGenerar = (reunion = null, archivo = null) => {
    setContextoV2({ reunion, archivo, minuteId: null, grabacion: null });
    window.location.hash = '#v2/generar';
  };

  const irADetalle = ({ reunion = null, minuteId = null, grabacion = null }) => {
    setContextoV2({ reunion, archivo: null, minuteId, grabacion });
    window.location.hash = '#v2/reunion';
  };

  const [entrando, setEntrando] = useState(false);
  const [errorLogin, setErrorLogin] = useState(null);

  const handleLogin = () => {
    setEntrando(true);
    setErrorLogin(null);

    instance.loginPopup(loginRequest)
      .catch(e => {
        // Antes esto solo iba a la consola: quien pulsaba el botón veía que no
        // pasaba nada y no tenía forma de saber por qué.
        const cancelado = /user_cancelled|popup_window_error|interaction_in_progress/i
          .test(e.errorCode || e.message || '');

        setErrorLogin(cancelado
          ? 'Se cerró la ventana de Microsoft antes de terminar. Vuelve a intentarlo; si tu navegador bloquea las ventanas emergentes, permíteselas a esta página.'
          : `No se pudo iniciar sesión: ${e.errorMessage || e.message}`);
      })
      .finally(() => setEntrando(false));
  };

  // ── El token con el que la API sabe quien eres ────────────────────────
  //
  // Hasta aqui la identidad viajaba en la URL —`?userId=...`— y el servidor se
  // la creia. Para las actas de cada quien dentro de una empresa pasaba; para
  // decidir quien puede subir plantillas, no: seria preguntarle al que entra si
  // tiene llave.
  //
  // Asi que se le da a la API el token que emitio Microsoft, y el servidor
  // comprueba su firma. Se entrega una FUNCION y no el token: caduca cada hora,
  // y uno capturado al arrancar estaria muerto a media mañana. MSAL lo renueva
  // solo cuando se le pide, y por eso se le pide en cada llamada.
  //
  // `acquireTokenSilent` no sale a la red si el que tiene todavia sirve.
  useEffect(() => {
    if (!isAuthenticated || !accounts[0]) {
      usarTokenDeSesion(null);
      return;
    }
    usarTokenDeSesion(async () => {
      const r = await instance.acquireTokenSilent({
        ...loginRequest, account: accounts[0]
      });
      // El de identidad, no el de Graph: el de Graph esta emitido PARA Graph y
      // esta API no tiene por que —ni puede— darlo por bueno.
      return r.idToken || null;
    });
  }, [isAuthenticated, accounts, instance]);

  // Va despues de todos los hooks y no arriba, donde se calcula `firma`: salir
  // antes cambiaria cuantos hooks se ejecutan entre una pintada y otra, que es
  // lo unico que React no perdona.
  if (firma) {
    return (
      <TemaProvider userId={isAuthenticated && accounts[0] ? userIdDeCuenta(accounts[0]) : null}>
        <FirmarActa token={firma} />
      </TemaProvider>
    );
  }

  if (isAuthenticated) {
    // El tema es de cada persona, así que el proveedor necesita saber quién es.
    // Solo envuelve a la v2: v1 tiene sus propios estilos en index.css y
    // pintarla con esta paleta requeriría reescribirlos.
    const userId = userIdDeCuenta(accounts[0]);

    return (
      <TemaProvider userId={userId}>
        {/* La configuración es una ventana encima, así que detrás tiene que
            haber algo: sin esto, su fondo translúcido dejaría ver una página en
            blanco y dejaría de parecer «encima de la aplicación» para parecer
            una pantalla más, mal pintada. */}
        {(vistaV2 === 'inicio' || vistaV2 === 'config') && (
          <Inicio onNuevaMinuta={irAGenerar} onAbrirDetalle={irADetalle} />
        )}
        {vistaV2 === 'reunion' && (
          <DetalleReunion
            reunion={contextoV2.reunion}
            minuteId={contextoV2.minuteId}
            grabacion={contextoV2.grabacion}
            onVolver={() => { window.location.hash = '#v2'; }}
            onGenerarConFormato={(reunion) => irAGenerar(reunion)}
          />
        )}
        {vistaV2 === 'config' && (
          <Configuracion onVolver={() => { window.location.hash = '#v2'; }} />
        )}
        {vistaV2 === 'generar' && (
          <GeneradorMinutaV2
            reunion={contextoV2.reunion}
            archivoInicial={contextoV2.archivo}
            onVolver={() => { window.location.hash = '#v2'; }}
          />
        )}
      </TemaProvider>
    );
  }

  return (
    <TemaProvider userId={null}>
      <Bienvenida onLogin={handleLogin} entrando={entrando} error={errorLogin} />
    </TemaProvider>
  );
};

export default AuthenticatedApp;